/**
 * Minimal REST client for `/v1/boards/{boardId}` — enough to fetch the
 * board metadata on bootstrap and submit a title PATCH from the
 * top-bar editor (Bug #3 in the 2026-05-09 SPA QA wave).
 *
 * Auth + concurrency contract:
 *   - Same cookie+CSRF model as the rest of the SPA: `credentials:
 *     'include'` ships the `__Host-gocanvas_at` cookie, and writes
 *     mirror the JS-readable `gocanvas_csrf` cookie into the
 *     `X-CSRF-Token` header (RFC 6749 §10.12 / FOR-54).
 *   - The board ID accepted on the URL can be either the 26-char ULID
 *     the SPA puts in routes or the 32-char hex form the REST POST
 *     returns; the backend's `requireBoardID` decodes both forms via
 *     `internal/codec/ulid.Decode`.
 *   - Updates carry `If-Match: <etag>` (FOR-100). A stale ETag yields
 *     412 with `Problem.type === 'urn:gocanvas:precondition:etag'`;
 *     callers should refetch and surface a "refreshed" toast rather
 *     than silently overwriting.
 */

import { setEffectiveRole } from '../permissions/effectiveRole.js';

export interface BoardWire {
  id: string;
  /**
   * Project whose tree owns this board (GOCANVAS-530/650 — replaced
   * `workspace_id`). Resolved server-side from the board's tree node, so it
   * is the same id `getTree` is keyed by. Absent on legacy rows that were
   * never mirrored into a project.
   */
  project_id?: string;
  owner_id?: string;
  title: string;
  description: string;
  version: number;
  /** RFC 7807-compatible JSON blob — opaque to the SPA. */
  settings: unknown;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
  /** FOR-173: backend-resolved role for the current principal. */
  effective_role?: 'owner' | 'editor' | 'commenter' | 'viewer';
}

export interface BoardFetchResult {
  board: BoardWire;
  /** Verbatim ETag header value (may carry the `W/` weak prefix). */
  etag: string;
}

export type BoardFetchError =
  | { kind: 'not_found' }
  | { kind: 'unauthorized' }
  | { kind: 'forbidden' }
  | { kind: 'server'; status: number; detail?: string }
  | { kind: 'network'; cause: unknown }
  | { kind: 'malformed' };

export type BoardUpdateError =
  | { kind: 'not_found' }
  | { kind: 'unauthorized' }
  | { kind: 'forbidden' }
  | { kind: 'precondition'; status: 412 }
  | { kind: 'server'; status: number; detail?: string }
  | { kind: 'network'; cause: unknown }
  | { kind: 'malformed' };

/** Read the `gocanvas_csrf` (or `__Host-gocanvas_csrf`) cookie value. */
export function readCsrfCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  for (const c of cookies) {
    const eq = c.indexOf('=');
    if (eq < 0) continue;
    const name = c.slice(0, eq).trim();
    if (name === 'gocanvas_csrf' || name === '__Host-gocanvas_csrf') {
      return decodeURIComponent(c.slice(eq + 1));
    }
  }
  return null;
}

/** Trim trailing slashes on the API base so URL composition is safe. */
function trimBase(apiBase: string): string {
  return apiBase.replace(/\/+$/, '');
}

/** Tagged Result-like wrapper so callers branch with a discriminator. */
export type BoardFetchOutcome =
  | { ok: true; result: BoardFetchResult }
  | { ok: false; error: BoardFetchError };

export type BoardUpdateOutcome =
  | { ok: true; result: BoardFetchResult }
  | { ok: false; error: BoardUpdateError };

export interface BoardsClientOptions {
  /** Defaults to `globalThis.fetch`. Tests inject vi.fn(). */
  fetchImpl?: typeof fetch;
  signal?: AbortSignal;
}

/**
 * GET /v1/boards/{boardId}. Resolves with the parsed board + ETag on
 * success, or a tagged error on every other outcome (the caller logs
 * + falls back to "Untitled board"; this never throws).
 */
export async function fetchBoard(
  apiBase: string,
  boardId: string,
  opts: BoardsClientOptions = {},
): Promise<BoardFetchOutcome> {
  const fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const url = `${trimBase(apiBase)}/v1/boards/${encodeURIComponent(boardId)}`;
  let resp: Response;
  try {
    resp = await fetchImpl(url, {
      method: 'GET',
      credentials: 'include',
      ...(opts.signal && { signal: opts.signal }),
      headers: { Accept: 'application/json' },
    });
  } catch (cause) {
    return { ok: false, error: { kind: 'network', cause } };
  }
  if (resp.status === 401) return { ok: false, error: { kind: 'unauthorized' } };
  if (resp.status === 403) return { ok: false, error: { kind: 'forbidden' } };
  if (resp.status === 404) return { ok: false, error: { kind: 'not_found' } };
  if (!resp.ok) {
    const detail = await safeReadDetail(resp);
    return {
      ok: false,
      error: { kind: 'server', status: resp.status, ...(detail !== undefined && { detail }) },
    };
  }
  const etag = resp.headers.get('ETag') ?? '';
  let board: BoardWire;
  try {
    board = (await resp.json()) as BoardWire;
  } catch {
    return { ok: false, error: { kind: 'malformed' } };
  }
  if (!board || typeof board.title !== 'string') {
    return { ok: false, error: { kind: 'malformed' } };
  }
  // FOR-173: cache the resolver verdict so SPA-side gates
  // (canWrite / topbar editor / toolbar) reflect reality.
  if (board.effective_role !== undefined) {
    setEffectiveRole(board.id, board.effective_role);
    if (boardId !== board.id) {
      setEffectiveRole(boardId, board.effective_role);
    }
  }
  return { ok: true, result: { board, etag } };
}

/**
 * PATCH /v1/boards/{boardId} with `{title}` and `If-Match`. Resolves
 * with the new board + new ETag on 200, or a tagged error otherwise.
 *
 * Stale ETag → `kind: 'precondition'` (412); the caller should refetch
 * and surface a "refreshed" toast. Network / 5xx errors should
 * propagate to a "try again" toast and the editor should restore the
 * previous title.
 */
export async function updateBoardTitle(
  apiBase: string,
  boardId: string,
  title: string,
  ifMatchEtag: string,
  csrfToken: string,
  opts: BoardsClientOptions = {},
): Promise<BoardUpdateOutcome> {
  const fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const url = `${trimBase(apiBase)}/v1/boards/${encodeURIComponent(boardId)}`;
  let resp: Response;
  try {
    resp = await fetchImpl(url, {
      method: 'PATCH',
      credentials: 'include',
      ...(opts.signal && { signal: opts.signal }),
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        // RFC 7232 §3.1: the value is opaque — pass through whatever
        // the server emitted on GET, including the `W/` weak prefix.
        'If-Match': ifMatchEtag,
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({ title }),
    });
  } catch (cause) {
    return { ok: false, error: { kind: 'network', cause } };
  }
  if (resp.status === 401) return { ok: false, error: { kind: 'unauthorized' } };
  if (resp.status === 403) return { ok: false, error: { kind: 'forbidden' } };
  if (resp.status === 404) return { ok: false, error: { kind: 'not_found' } };
  if (resp.status === 412) return { ok: false, error: { kind: 'precondition', status: 412 } };
  if (!resp.ok) {
    const detail = await safeReadDetail(resp);
    return {
      ok: false,
      error: { kind: 'server', status: resp.status, ...(detail !== undefined && { detail }) },
    };
  }
  const etag = resp.headers.get('ETag') ?? '';
  let board: BoardWire;
  try {
    board = (await resp.json()) as BoardWire;
  } catch {
    return { ok: false, error: { kind: 'malformed' } };
  }
  if (!board || typeof board.title !== 'string') {
    return { ok: false, error: { kind: 'malformed' } };
  }
  // FOR-173: cache the resolver verdict from updateBoardTitle response
  // (PATCH always returns the post-update boardWire).
  if (board.effective_role !== undefined) {
    setEffectiveRole(board.id, board.effective_role);
  }
  return { ok: true, result: { board, etag } };
}

/** Best-effort RFC 7807 detail extraction; never throws. */
async function safeReadDetail(resp: Response): Promise<string | undefined> {
  try {
    const j = (await resp.json()) as { detail?: unknown };
    return typeof j?.detail === 'string' ? j.detail : undefined;
  } catch {
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// FOR-196 — list / create / delete used by the Workspaces page
// ---------------------------------------------------------------------------

export interface BoardSummary {
  id: string;
  title: string;
  updated_at: string;
  /** Backend pending FOR-173 list/create response support. When
   *  absent, callers should default to assuming write access and
   *  rely on backend 403 to enforce; this matches the "menu shown,
   *  backend rejects" graceful degradation pattern. */
  effective_role?: 'owner' | 'editor' | 'commenter' | 'viewer';
}

export type BoardsListError =
  | { kind: 'unauthorized' }
  | { kind: 'server'; status: number; detail?: string }
  | { kind: 'network'; cause: unknown }
  | { kind: 'malformed' };

export type BoardsListOutcome =
  | { ok: true; items: BoardSummary[] }
  | { ok: false; error: BoardsListError };

/**
 * POST /v1/boards body (GOCANVAS-530/650).
 *
 * `projectId` is required: the server creates the board, its owner
 * membership and its `kind=board` tree node in one transaction inside that
 * project, and there is no workspace fallback any more. `parentId` /
 * `sortKey` are optional tree placement — omitted, the board lands at the
 * top level after the current last sibling.
 *
 * The server decodes the body with DisallowUnknownFields, so this shape is
 * the whole contract: no `workspace_id` dual-write during a transition.
 */
export interface CreateBoardBody {
  title: string;
  projectId: string;
  parentId?: string;
  sortKey?: string;
}

export type BoardCreateError =
  | { kind: 'unauthorized' }
  | { kind: 'forbidden' }
  | { kind: 'validation'; detail?: string }
  | { kind: 'server'; status: number; detail?: string }
  | { kind: 'network'; cause: unknown }
  | { kind: 'malformed' };

export type BoardCreateOutcome =
  | { ok: true; board: BoardSummary }
  | { ok: false; error: BoardCreateError };

export type BoardDeleteError =
  | { kind: 'unauthorized' }
  | { kind: 'forbidden' }
  | { kind: 'not_found' }
  | { kind: 'server'; status: number; detail?: string }
  | { kind: 'network'; cause: unknown };

export type BoardDeleteOutcome =
  | { ok: true }
  | { ok: false; error: BoardDeleteError };

export async function listBoards(
  apiBase: string,
  opts: BoardsClientOptions = {},
): Promise<BoardsListOutcome> {
  const fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const url = `${trimBase(apiBase)}/v1/boards`;
  let resp: Response;
  try {
    resp = await fetchImpl(url, {
      method: 'GET',
      credentials: 'include',
      ...(opts.signal && { signal: opts.signal }),
      headers: { Accept: 'application/json' },
    });
  } catch (cause) {
    return { ok: false, error: { kind: 'network', cause } };
  }
  if (resp.status === 401) return { ok: false, error: { kind: 'unauthorized' } };
  if (!resp.ok) {
    const detail = await safeReadDetail(resp);
    return {
      ok: false,
      error: { kind: 'server', status: resp.status, ...(detail !== undefined && { detail }) },
    };
  }
  let body: { items?: unknown };
  try {
    body = (await resp.json()) as { items?: unknown };
  } catch {
    return { ok: false, error: { kind: 'malformed' } };
  }
  const items = Array.isArray(body.items)
    ? body.items.filter(isBoardSummary)
    : [];
  // FOR-173: populate the per-board cache so subsequent canWrite()
  // / topbar gates can answer without an extra round-trip.
  for (const b of items) {
    if (b.effective_role !== undefined) setEffectiveRole(b.id, b.effective_role);
  }
  return { ok: true, items };
}

export async function createBoard(
  apiBase: string,
  body: CreateBoardBody,
  opts: BoardsClientOptions = {},
): Promise<BoardCreateOutcome> {
  const fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const url = `${trimBase(apiBase)}/v1/boards`;
  const csrf = readCsrfCookie() ?? '';
  // Wire names are snake_case; optional placement fields are omitted rather
  // than sent as undefined — the server rejects unknown/extra fields.
  const wire: Record<string, string> = { title: body.title, project_id: body.projectId };
  if (body.parentId !== undefined) wire['parent_id'] = body.parentId;
  if (body.sortKey !== undefined) wire['sort_key'] = body.sortKey;
  let resp: Response;
  try {
    resp = await fetchImpl(url, {
      method: 'POST',
      credentials: 'include',
      ...(opts.signal && { signal: opts.signal }),
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': csrf,
      },
      body: JSON.stringify(wire),
    });
  } catch (cause) {
    return { ok: false, error: { kind: 'network', cause } };
  }
  if (resp.status === 401) return { ok: false, error: { kind: 'unauthorized' } };
  if (resp.status === 403) return { ok: false, error: { kind: 'forbidden' } };
  // 422 = failed field validation, 400 = a body the server refused to decode
  // (unknown field, unparsable project_id). Both are "this request was
  // wrong", which is what the create modal renders; a 400 shown as a
  // generic network error would send the user to retry the same bad body.
  if (resp.status === 400 || resp.status === 422) {
    const detail = await safeReadDetail(resp);
    return {
      ok: false,
      error: { kind: 'validation', ...(detail !== undefined && { detail }) },
    };
  }
  if (!resp.ok) {
    const detail = await safeReadDetail(resp);
    return {
      ok: false,
      error: { kind: 'server', status: resp.status, ...(detail !== undefined && { detail }) },
    };
  }
  let board: BoardSummary;
  try {
    board = (await resp.json()) as BoardSummary;
  } catch {
    return { ok: false, error: { kind: 'malformed' } };
  }
  if (!isBoardSummary(board)) {
    return { ok: false, error: { kind: 'malformed' } };
  }
  // FOR-173: creator is the owner — cache it so the redirect to
  // /board/<id> renders edit affordances without waiting for a GET.
  if (board.effective_role !== undefined) {
    setEffectiveRole(board.id, board.effective_role);
  }
  return { ok: true, board };
}

export async function deleteBoard(
  apiBase: string,
  boardId: string,
  opts: BoardsClientOptions = {},
): Promise<BoardDeleteOutcome> {
  const fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const url = `${trimBase(apiBase)}/v1/boards/${encodeURIComponent(boardId)}`;
  const csrf = readCsrfCookie() ?? '';
  let resp: Response;
  try {
    resp = await fetchImpl(url, {
      method: 'DELETE',
      credentials: 'include',
      ...(opts.signal && { signal: opts.signal }),
      headers: { 'X-CSRF-Token': csrf },
    });
  } catch (cause) {
    return { ok: false, error: { kind: 'network', cause } };
  }
  if (resp.status === 401) return { ok: false, error: { kind: 'unauthorized' } };
  if (resp.status === 403) return { ok: false, error: { kind: 'forbidden' } };
  if (resp.status === 404) return { ok: false, error: { kind: 'not_found' } };
  if (!resp.ok) {
    const detail = await safeReadDetail(resp);
    return {
      ok: false,
      error: { kind: 'server', status: resp.status, ...(detail !== undefined && { detail }) },
    };
  }
  return { ok: true };
}

function isBoardSummary(v: unknown): v is BoardSummary {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  if (
    typeof o['id'] !== 'string' ||
    typeof o['title'] !== 'string' ||
    typeof o['updated_at'] !== 'string'
  ) {
    return false;
  }
  // effective_role optional — backend pending FOR-173.
  if (
    o['effective_role'] !== undefined &&
    o['effective_role'] !== 'owner' &&
    o['effective_role'] !== 'editor' &&
    o['effective_role'] !== 'commenter' &&
    o['effective_role'] !== 'viewer'
  ) {
    return false;
  }
  return true;
}
